/**
 * DecoratorQuoteFlow - Quote request for Event Decoration services
 * Theme-based with before/after gallery
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { 
  Calendar as CalendarIcon, Palette, Check, ChevronRight, Loader2,
  MapPin, Sparkles, Image, Upload
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme options
const THEME_OPTIONS = [
  { id: 'classic', name: 'Classic Elegance', description: 'Timeless, sophisticated decor', colors: ['white', 'gold', 'ivory'] },
  { id: 'rustic', name: 'Rustic Charm', description: 'Natural, earthy elements', colors: ['brown', 'green', 'cream'] },
  { id: 'modern', name: 'Modern Minimalist', description: 'Clean lines, contemporary', colors: ['black', 'white', 'silver'] },
  { id: 'tropical', name: 'Tropical Paradise', description: 'Bright, vibrant colors', colors: ['pink', 'green', 'yellow'] },
  { id: 'vintage', name: 'Vintage Romance', description: 'Nostalgic, romantic feel', colors: ['blush', 'gold', 'dusty rose'] },
  { id: 'custom', name: 'Custom Theme', description: 'Your own unique vision', colors: [] }
];

// Decoration areas
const DECORATION_AREAS = [
  { id: 'entrance', name: 'Entrance / Welcome Area', price: 25000 },
  { id: 'stage', name: 'Stage / Main Backdrop', price: 75000 },
  { id: 'tables', name: 'Table Centerpieces', price: 15000, perUnit: true },
  { id: 'aisle', name: 'Aisle Decoration', price: 35000 },
  { id: 'ceiling', name: 'Ceiling / Draping', price: 50000 },
  { id: 'photo_booth', name: 'Photo Booth Setup', price: 30000 },
  { id: 'outdoor', name: 'Outdoor / Garden Area', price: 45000 },
  { id: 'full_venue', name: 'Full Venue Package', price: 200000 }
];

const EVENT_TYPES = [
  { value: 'weddings', label: 'Wedding' },
  { value: 'birthday', label: 'Birthday Party' },
  { value: 'corporate', label: 'Corporate Event' },
  { value: 'baby_shower', label: 'Baby Shower' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'religious', label: 'Religious/Cultural' },
  { value: 'other', label: 'Other' }
];

export default function DecoratorQuoteFlow({ open, onClose, provider, onQuoteSubmitted }) {
  const [step, setStep] = useState(1);
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [tableCount, setTableCount] = useState('10');
  const [selectedDate, setSelectedDate] = useState(null);
  const [eventType, setEventType] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [venueName, setVenueName] = useState('');
  const [colorPreferences, setColorPreferences] = useState('');
  const [inspirationNotes, setInspirationNotes] = useState('');
  const [budget, setBudget] = useState('');
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);

  const currency = provider?.currency || 'Rs';

  const calculateEstimate = () => {
    let total = 0;
    selectedAreas.forEach(areaId => {
      const area = DECORATION_AREAS.find(a => a.id === areaId);
      if (area) {
        if (area.perUnit && areaId === 'tables') {
          total += area.price * parseInt(tableCount || 0);
        } else {
          total += area.price;
        }
      }
    });
    return total;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const quoteData = {
        provider_id: provider?.id,
        event_date: format(selectedDate, 'yyyy-MM-dd'),
        event_type: eventType,
        theme: selectedTheme,
        decoration_areas: selectedAreas,
        table_count: selectedAreas.includes('tables') ? parseInt(tableCount) : null,
        venue_name: venueName,
        venue_address: venueAddress,
        color_preferences: colorPreferences,
        inspiration_notes: inspirationNotes,
        budget_indication: budget,
        estimated_cost: calculateEstimate(),
        contact: contactInfo
      };

      const response = await fetch(`${API_URL}/api/events/quotes/decoration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quoteData)
      });

      if (response.ok) {
        toast.success('Quote request sent! The decorator will respond within 24 hours with a detailed proposal.');
        onQuoteSubmitted?.();
        onClose();
      } else {
        throw new Error('Quote request failed');
      }
    } catch (error) {
      toast.error('Failed to send quote request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Choose Your Theme</h3>
            <p className="text-sm text-gray-500">Select a style that matches your vision</p>
            
            <div className="grid grid-cols-2 gap-3">
              {THEME_OPTIONS.map(theme => (
                <button
                  key={theme.id}
                  onClick={() => setSelectedTheme(theme.id)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    selectedTheme === theme.id
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{theme.name}</p>
                      <p className="text-xs text-gray-500">{theme.description}</p>
                      {theme.colors.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {theme.colors.map(color => (
                            <div 
                              key={color} 
                              className="w-4 h-4 rounded-full border"
                              style={{ backgroundColor: color === 'white' ? '#f5f5f5' : color }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedTheme === theme.id && (
                      <Check className="w-5 h-5 text-teal-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            {selectedTheme === 'custom' && (
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <label className="text-sm font-medium text-teal-800">Describe your vision</label>
                <Textarea
                  value={colorPreferences}
                  onChange={(e) => setColorPreferences(e.target.value)}
                  placeholder="Colors, mood, specific elements you want..."
                  rows={3}
                  className="mt-2"
                />
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Areas to Decorate</h3>
            <p className="text-sm text-gray-500">Select all areas you need decorated</p>
            
            <div className="space-y-2">
              {DECORATION_AREAS.map(area => (
                <label 
                  key={area.id} 
                  className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedAreas.includes(area.id)}
                      onCheckedChange={(checked) => {
                        setSelectedAreas(prev => 
                          checked ? [...prev, area.id] : prev.filter(id => id !== area.id)
                        );
                      }}
                    />
                    <div>
                      <p className="font-medium">{area.name}</p>
                      {area.perUnit && (
                        <p className="text-xs text-gray-500">Price per table</p>
                      )}
                    </div>
                  </div>
                  <span className="font-bold text-teal-600">
                    {currency} {area.price.toLocaleString()}
                    {area.perUnit && <span className="text-xs font-normal">/each</span>}
                  </span>
                </label>
              ))}
            </div>

            {selectedAreas.includes('tables') && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="text-sm font-medium">Number of Tables</label>
                <Input
                  type="number"
                  value={tableCount}
                  onChange={(e) => setTableCount(e.target.value)}
                  min={1}
                  className="mt-2"
                />
              </div>
            )}

            {selectedAreas.length > 0 && (
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <div className="flex justify-between items-center">
                  <span className="text-teal-800 font-medium">Estimated Starting Price</span>
                  <span className="text-xl font-bold text-teal-600">
                    {currency} {calculateEstimate().toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-teal-600 mt-1">
                  Final price may vary based on venue size and custom requirements
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Event Details</h3>
            
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Event Date</label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date() || date > addDays(new Date(), 365)}
                  className="rounded-md border"
                />
              </div>
              
              <div className="flex-1 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Event Type</label>
                  <Select value={eventType} onValueChange={setEventType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Venue Name</label>
                  <Input
                    value={venueName}
                    onChange={(e) => setVenueName(e.target.value)}
                    placeholder="e.g., Grand Ballroom"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Venue Address</label>
                  <Input
                    value={venueAddress}
                    onChange={(e) => setVenueAddress(e.target.value)}
                    placeholder="Full address"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Inspiration & Budget</h3>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Share Your Inspiration</label>
              <Textarea
                value={inspirationNotes}
                onChange={(e) => setInspirationNotes(e.target.value)}
                placeholder="Describe any specific ideas, Pinterest boards, or reference images..."
                rows={4}
              />
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Upload inspiration images (coming soon)</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Budget Indication</label>
              <Select value={budget} onValueChange={setBudget}>
                <SelectTrigger>
                  <SelectValue placeholder="Select budget range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="under_50k">Under Rs. 50,000</SelectItem>
                  <SelectItem value="50k_100k">Rs. 50,000 - 100,000</SelectItem>
                  <SelectItem value="100k_200k">Rs. 100,000 - 200,000</SelectItem>
                  <SelectItem value="200k_500k">Rs. 200,000 - 500,000</SelectItem>
                  <SelectItem value="over_500k">Over Rs. 500,000</SelectItem>
                  <SelectItem value="flexible">Flexible / Discuss</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Contact & Submit</h3>
            
            <div className="space-y-3">
              <Input
                placeholder="Your full name *"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
              />
              <Input
                placeholder="Phone number *"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
              />
              <Input
                type="email"
                placeholder="Email address *"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <h4 className="font-medium">Quote Request Summary</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Theme</span>
                  <span className="capitalize">{selectedTheme}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Areas</span>
                  <span>{selectedAreas.length} selected</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date</span>
                  <span>{selectedDate && format(selectedDate, 'MMM d, yyyy')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Event</span>
                  <span className="capitalize">{eventType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Venue</span>
                  <span>{venueName || 'TBC'}</span>
                </div>
                <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                  <span>Estimated</span>
                  <span className="text-teal-600">{currency} {calculateEstimate().toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <Sparkles className="w-4 h-4 inline mr-2" />
                The decorator will provide a detailed proposal with exact pricing after reviewing your requirements.
              </p>
            </div>
          </div>
        );
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-teal-600" />
            Request Decoration Quote
          </DialogTitle>
          <DialogDescription>{provider.provider_name}</DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                s < step ? 'bg-teal-600 text-white' :
                s === step ? 'bg-teal-100 text-teal-700 border-2 border-teal-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? <Check className="w-3 h-3" /> : s}
              </div>
              {s < 5 && <div className={`flex-1 h-1 ${s < step ? 'bg-teal-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {renderStep()}

        <div className="flex justify-between pt-4 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>Back</Button>
          ) : <div />}
          
          {step < 5 ? (
            <Button 
              onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 1 && !selectedTheme) ||
                (step === 2 && selectedAreas.length === 0) ||
                (step === 3 && (!selectedDate || !eventType || !venueAddress))
              }
              className="bg-teal-600 hover:bg-teal-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={loading || !contactInfo.name || !contactInfo.phone || !contactInfo.email}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Send Quote Request
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
